/* eslint-disable @angular-eslint/no-input-rename */
// TODO: Remove this eslint disable after fixing the aliases (ceTooltip, ceTooltipDisplayOnClick etc...) from camelCase to kebab-case
/* eslint-disable @angular-eslint/directive-selector */

import { ConnectedPosition, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AfterViewInit, ComponentRef, Directive, ElementRef, HostListener, Input, input, OnDestroy } from '@angular/core';
import { CeTooltipComponent } from './ce-tooltip.component';
import { CeOverlayContainerService } from '../ce-overlay/ce-overlay-container.service';
import { CePopoverCustomOverlay } from '../ce-popover/ce-popover-trigger.directive';

export type CeTooltipPosition = 'above' | 'below' | 'left' | 'right' | 'auto';

@Directive({
    selector: '[ceTooltip]',
    standalone: true,
})
export class CeTooltipDirective implements OnDestroy, AfterViewInit {
    /**
     * @type {('string')}
     *
     * @description
     * The message to be displayed in the tooltip.
     */
    @Input({ required: true, alias: 'ceTooltip' }) message!: string;
    /**
     * @type {('boolean')}
     * @default false
     *
     * @description
     * Whether the tooltip should be displayed on hover or on click.
     */
    @Input('ceTooltipDisplayOnClick') displayOnClick = false;
    /**
     * @type {('number' | 'null')}
     * @requires displayOnClick = true
     * @default null
     *
     * @description
     * The default delay in ms before hiding the tooltip after it has been displayed.
     */
    @Input('ceTooltipHideDelay') hideDelay: number | null = null;
    /**
     * @type {('string' | 'null')}
     *
     * @description
     * Classes to be passed to the tooltip.
     */
    @Input('ceTooltipClassList') classList!: string;
    /**
     * @type {('CeTooltipPosition' | ('above' | 'below' | 'left' | 'right' | 'auto'))}
     *
     * @description
     * Allows the user to define the position of the tooltip relative to the parent element.
     */
    ceTooltipPosition = input<CeTooltipPosition>('auto');

    /**
     * Defines a special use case: used inside <dialog> or in full screen sized component. Affects the appearance place in DOM.
     */
    ceTooltipCustomOverlay = input<CePopoverCustomOverlay | undefined>(undefined);

    /**
     * Define the element you want to contain the tooltip to.
     */
    ceTooltipContainer = input<string>();

    #tooltipRef?: ComponentRef<CeTooltipComponent>;
    #overlayRef!: OverlayRef;

    #tooltipDelayTimeout?: number;
    #transitionTimeout?: number;

    readonly #scrollListeners: { element: Element; listener: () => void }[] = [];

    #tooltipOverlayContainer!: HTMLDivElement;

    constructor(
        private readonly elRef: ElementRef,
        private readonly overlay: Overlay,
        private readonly overlayPositionBuilder: OverlayPositionBuilder,
        private readonly tooltipContainerService: CeOverlayContainerService
    ) {}

    ngAfterViewInit(): void {
        const positionStrategy = this.overlayPositionBuilder
            .flexibleConnectedTo(this.elRef)
            .withFlexibleDimensions(false)
            .withPositions(this.#getPositionStrategy());

        this.#overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            hasBackdrop: false,
            panelClass: `ce-overlay-tooltip`,
        });

        // TODO: Check withScrollableContainers() method from Angular CDK Overlay and maybe use it instead of the custom implementation
        this.#attachScrollListenerToOtherOverlayElements();
    }

    ngOnDestroy(): void {
        this.#overlayRef?.dispose();
        this.#tooltipDelayTimeout && clearTimeout(this.#tooltipDelayTimeout);
        this.#transitionTimeout && clearTimeout(this.#transitionTimeout);

        // Remove the scroll event listeners from the other overlay elements on destroy
        if (this.#scrollListeners?.length > 0) {
            this.#scrollListeners.forEach(({ element, listener }) => {
                element.removeEventListener('scroll', listener);
            });
        }
    }

    @HostListener('mouseenter')
    show() {
        //attach the component if it has not already attached to the overlay
        if (!this.displayOnClick) {
            this.#showTooltip();
        }
    }

    @HostListener('mouseleave')
    hide() {
        if (!this.displayOnClick) {
            this.#hideTooltip();
        }
    }

    // Shows the tooltip when the element is clicked (if hover display is disabled)
    @HostListener('click', ['$event']) onClick(event: MouseEvent) {
        event.stopPropagation();
        if (this.displayOnClick) {
            this.#overlayRef?.hasAttached() ? this.#hideTooltip() : this.#showTooltip();
        }
    }

    // Hides the tooltip when the user clicks outside of the tooltip when it's displayed
    @HostListener('document:click', ['$event']) onDocumentClick(event: MouseEvent) {
        event.stopPropagation();
        this.#hideTooltip();
    }

    #showTooltip() {
        // Clear the timeout for hideDelay if it's set before showing the tooltip.
        // This is to prevent the tooltip from being hidden if the user hovers over the element again before the delay has passed.
        this.#tooltipDelayTimeout = this.#clearTimeoutAndReset(this.#tooltipDelayTimeout);
        // Clear the timeout for transition if it's set before showing the tooltip.
        // This is to prevent the tooltip from not being able to be displayed when the hide animation is already ongoing from previous tooltip.
        this.#transitionTimeout = this.#clearTimeoutAndReset(this.#transitionTimeout);

        // Detach any existing tooltip
        if (this.#overlayRef.hasAttached()) {
            if (this.#tooltipOverlayContainer) {
                this.tooltipContainerService.removeOverlayContainer(this.#tooltipOverlayContainer);
            }
            this.#overlayRef.detach();
        }

        const containerSelector = this.ceTooltipContainer();

        if (containerSelector && typeof containerSelector === 'string') {
            // If a specific container is provided, create the overlay container within that container
            this.#tooltipOverlayContainer = this.tooltipContainerService.createOverlayContainerWithinContainer({
                className: 'tooltip-overlay-container',
                container: containerSelector,
            });
            this.#tooltipOverlayContainer.appendChild(this.#overlayRef.hostElement);
        } else {
            // Create a new entry point for dialog to be rendered over other overlay entry points
            this.#tooltipOverlayContainer =
                this.tooltipContainerService.createOverlayContainer('tooltip-overlay-container');
            if (!this.ceTooltipCustomOverlay()?.enabled) {
                this.#tooltipOverlayContainer.appendChild(this.#overlayRef.hostElement);
            } else {
                // If tooltip is used inside <dialog> or with a full screen mode, it needs be attached to a custom overlay.
                // If it were to appear normally, it wouldn't be visible.
                document
                    .querySelector(`${this.ceTooltipCustomOverlay()?.overlaySelector}`)
                    ?.appendChild(this.#overlayRef.hostElement);
            }
        }

        // Create a new tooltip component and attach it to the overlay
        const tooltipPortal = new ComponentPortal(CeTooltipComponent);
        this.#tooltipRef = this.#overlayRef.attach(tooltipPortal);

        // Set the tooltip text and style
        this.#tooltipRef.instance.text = this.message;
        this.#tooltipRef.instance.classList = this.classList;

        // Hide the tooltip after the delay if it's set
        if (this.hideDelay != null) {
            this.#tooltipDelayTimeout = setTimeout(() => this.#hideTooltip(), this.hideDelay) as unknown as number; // TODO: refactor the typing for the setTimeout()
        }
    }

    #hideTooltip() {
        if (this.#tooltipRef) {
            // Set the hide property of the tooltip component to true to trigger the fade-out transition before detaching the tooltip
            this.#tooltipRef.instance.hide = true;

            // Clear the tooltipDelayTimeout if it's set
            this.#tooltipDelayTimeout = this.#clearTimeoutAndReset(this.#tooltipDelayTimeout);

            // Detach the tooltip after the transition has finished
            this.#transitionTimeout = setTimeout(() => {
                if (this.#tooltipOverlayContainer) {
                    this.tooltipContainerService.removeOverlayContainer(this.#tooltipOverlayContainer);
                }
                this.#overlayRef.detach();
                this.#tooltipRef = undefined;
            }, 300) as unknown as number; // TODO: refactor the typing for the setTimeout()
        }
    }

    #attachScrollListenerToOtherOverlayElements() {
        // Get all the dialog elements in the DOM.
        // This is to ensure that the tooltip is repositioned when the dialog is scrolled.
        // This is necessary because the dialog is not a child of the overlay container.
        // Remember to update the selector if the dialog / wizard panel class changes or add new classes if necessary.
        const elements = document.querySelectorAll('.cdk-overlay-container .CeWizardPanel, .modal-overlay-container');

        // Add a scroll event listener to the elements
        elements?.forEach((element) => {
            const listener = () => {
                if (this.#overlayRef && this.#overlayRef.hasAttached()) {
                    this.#overlayRef.updatePosition();
                }
            };
            element.addEventListener('scroll', listener);
            this.#scrollListeners.push({ element, listener });
        });
    }

    #getPositionStrategy(): ConnectedPosition[] {
        let positionStrategy: ConnectedPosition[];
        switch (this.ceTooltipPosition()) {
            case 'above':
                positionStrategy = [
                    {
                        originX: 'center',
                        originY: 'top',
                        overlayX: 'center',
                        overlayY: 'bottom',
                    },
                ];
                break;
            case 'below':
                positionStrategy = [
                    {
                        originX: 'center',
                        originY: 'bottom',
                        overlayX: 'center',
                        overlayY: 'top',
                    },
                ];
                break;
            case 'left':
                positionStrategy = [
                    {
                        originX: 'start',
                        originY: 'center',
                        overlayX: 'end',
                        overlayY: 'center',
                    },
                ];
                break;
            case 'right':
                positionStrategy = [
                    {
                        originX: 'end',
                        originY: 'center',
                        overlayX: 'start',
                        overlayY: 'center',
                    },
                ];
                break;
            default:
                positionStrategy = [
                    {
                        originX: 'center',
                        originY: 'top',
                        overlayX: 'center',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'center',
                        originY: 'bottom',
                        overlayX: 'center',
                        overlayY: 'top',
                    },
                ];
                break;
        }

        return positionStrategy;
    }

    #clearTimeoutAndReset(timeout: number | undefined): number | undefined {
        if (timeout) {
            clearTimeout(timeout);
            timeout = undefined;
        }
        return timeout;
    }
}
